import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  EncerrarMinhaSessao,
  EncerrarMinhasOutrasSessoes,
  ListarMinhasSessoesAtivas,
  UsuarioLoginDto,
} from './sessoes.service';
import Links from '@/shared/constants/navegacao';
import { BiInfoCircle, BiLogOut, BiXCircle } from 'react-icons/bi';

const LIMITE_SESSOES_ATIVAS = 2;
const LIMITE_REGISTROS_SESSAO = 5;

function dataReferenciaSessao(sessao: UsuarioLoginDto): number {
  const raw = sessao.ultimoAcesso || sessao.logadoEm;
  return raw ? new Date(raw).getTime() : 0;
}

export default function MinhasSessoesCard() {
  const [sessoes, setSessoes] = useState<UsuarioLoginDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [encerrando, setEncerrando] = useState<number | 'outras' | null>(null);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const dados = await ListarMinhasSessoesAtivas();
      setSessoes(dados.sort((a, b) => dataReferenciaSessao(b) - dataReferenciaSessao(a)));
    } catch (error: any) {
      console.error('Erro ao carregar suas sessões:', error);
      toast.error(error?.message || 'Erro ao carregar suas sessões');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const handleEncerrarSessao = async (sessao: UsuarioLoginDto) => {
    if (!sessao.ativo) return;

    if (sessao.atual) {
      const ok = window.confirm(
        'Esta é a sessão atual. Encerrá-la fará você sair do sistema. Deseja continuar?',
      );
      if (!ok) return;
    } else if (!window.confirm('Tem certeza que deseja encerrar esta sessão?')) {
      return;
    }

    try {
      setEncerrando(sessao.id);
      await EncerrarMinhaSessao(sessao.id);
      if (sessao.atual) {
        window.location.href = Links.Auth_Logout;
        return;
      }
      toast.success('Sessão encerrada com sucesso!');
      carregarDados();
    } catch (error: any) {
      console.error('Erro ao encerrar sessão:', error);
      toast.error(error?.message || 'Erro ao encerrar sessão');
    } finally {
      setEncerrando(null);
    }
  };

  const handleEncerrarOutras = async () => {
    if (!window.confirm('Encerrar todas as outras sessões ativas, mantendo apenas a atual?')) {
      return;
    }

    try {
      setEncerrando('outras');
      await EncerrarMinhasOutrasSessoes();
      toast.success('Demais sessões ativas encerradas.');
      carregarDados();
    } catch (error: any) {
      console.error('Erro ao encerrar demais sessões:', error);
      toast.error(error?.message || 'Erro ao encerrar demais sessões');
    } finally {
      setEncerrando(null);
    }
  };

  const formatarData = (data?: string | null) => {
    if (!data) return '-';
    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const ativas = sessoes.filter((s) => s.ativo);
  const outrasAtivas = ativas.filter((s) => !s.atual).length;

  return (
    <div className="card">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <h5 className="mb-0">
          Minhas sessões
          {!loading && (
            <span className="badge bg-primary ms-2">
              {ativas.length}/{LIMITE_SESSOES_ATIVAS} ativas
            </span>
          )}
        </h5>
        <button
          type="button"
          className="btn btn-sm btn-warning"
          onClick={() => void handleEncerrarOutras()}
          disabled={loading || outrasAtivas === 0 || encerrando !== null}
          title="Encerrar todas as outras sessões ativas"
        >
          {encerrando === 'outras' ? (
            <span className="spinner-border spinner-border-sm me-1" role="status" />
          ) : (
            <BiLogOut size={18} className="me-1" />
          )}
          Encerrar outras sessões
        </button>
      </div>
      <div className="card-body pb-2">
        <div className="alert alert-secondary d-flex align-items-start gap-2 mb-3" role="status">
          <BiInfoCircle size={20} className="flex-shrink-0 mt-1" />
          <div className="small mb-0">
            No máximo <strong>{LIMITE_SESSOES_ATIVAS} sessões ativas</strong>. No {LIMITE_SESSOES_ATIVAS + 1}º
            login, a mais antiga é encerrada. Exibimos as últimas{' '}
            <strong>{LIMITE_REGISTROS_SESSAO} sessões</strong>.
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-4">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        ) : sessoes.length === 0 ? (
          <p className="text-muted mb-0">Nenhuma sessão encontrada.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-hover mb-0">
              <thead>
                <tr>
                  <th>IP</th>
                  <th>Login em</th>
                  <th>Último Acesso</th>
                  <th>Logout em</th>
                  <th>Status</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sessoes.map((sessao) => (
                  <tr key={sessao.id}>
                    <td>
                      <span
                        title={[sessao.ipOrigem, sessao.userAgent].filter(Boolean).join('\n') || undefined}
                      >
                        {sessao.ipOrigem || '-'}
                      </span>
                    </td>
                    <td>{formatarData(sessao.logadoEm)}</td>
                    <td>{formatarData(sessao.ultimoAcesso)}</td>
                    <td>{formatarData(sessao.deslogadoEm)}</td>
                    <td>
                      {sessao.atual ? (
                        <span className="badge bg-success">Sessão atual</span>
                      ) : sessao.ativo ? (
                        <span className="badge bg-info text-dark">Outro dispositivo</span>
                      ) : (
                        <span className="badge bg-secondary">Encerrada</span>
                      )}
                    </td>
                    <td className="text-center">
                      {sessao.ativo ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => void handleEncerrarSessao(sessao)}
                          disabled={encerrando !== null}
                          title={sessao.atual ? 'Encerrar e sair' : 'Encerrar esta sessão'}
                        >
                          {encerrando === sessao.id ? (
                            <span className="spinner-border spinner-border-sm" role="status" />
                          ) : (
                            <BiXCircle size={18} />
                          )}
                        </button>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
